import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import "../styles/UserForm.css";
import UserForm from "../components/UserForm"; // adjust path if needed
import { validateEmail } from "../utils/validators";

const UserManagement = ({ auth_user }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState({
    username: ""
  });

  // edit modal + form state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [username, setUsername] = useState("");
  const [designation, setDesignation] = useState("");
  const [location, setLocation] = useState([]); // array of location values
  const [outlets, setOutlets] = useState([]);   // array of outlet values
  const [userType, setUserType] = useState("");

  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedOutletOptions, setSelectedOutletOptions] = useState([]);

  // locations admin can use
  const allowedLocations = useMemo(() => {
    return Array.isArray(auth_user.locations)
      ? auth_user.locations
      : [auth_user.locations];
  }, [auth_user]);

  const locationOptions = useMemo(
    () =>
      allowedLocations.map((loc) => ({
        value: loc,
        label: loc,
      })),
    [allowedLocations]
  );

  // branches grouped by location from backend
  const allOutletsByLocation = useMemo(() => {
    return auth_user?.branches;
  }, [auth_user]);

  // grouped outlet options for current selected locations in the modal
  const groupedOutletOptions = useMemo(() => {
    if (!location.length) return [];
    return location.map((loc) => ({
      label: loc,
      options: (allOutletsByLocation[loc] || []).map((outlet) => ({
        value: outlet,
        label: outlet,
        location: loc,
      })),
    }));
  }, [location, allOutletsByLocation]);

  const allOutletOptionsFlat = useMemo(
    () => groupedOutletOptions.flatMap((g) => g.options),
    [groupedOutletOptions]
  );

  const allOutletValues = useMemo(
    () => allOutletOptionsFlat.map((o) => o.value),
    [allOutletOptionsFlat]
  );

  // location select options with "Select All"
  const locationOptionsWithSelectAll = useMemo(
    () => [
      { label: "Select All", value: "__ALL__" },
      ...locationOptions,
    ],
    [locationOptions]
  );

  // outlet select options with "Select All" + grouped options
  const outletOptionsWithSelectAll = useMemo(() => {
    if (!allOutletValues.length) {
      return groupedOutletOptions;
    }
    return [
      { label: "Select All", value: "__ALL__" },
      ...groupedOutletOptions,
    ];
  }, [allOutletValues, groupedOutletOptions]);

  // Fetch users
  const fetchUsers = async () => {
    try {
      const response = await axios.get(`/api/users/${auth_user.id}`);
      if (response.data) {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data.data || [];
        setUsers(list);
        setFilteredUsers(list);
      }
    } catch (err) {
      if (err.response) {
        alert(err.response.data.error.message);
      } else {
        alert(err);
      }
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle search
  useEffect(() => {
    const filtered = users.filter((u) =>
      u.username.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [search, users]);

  // ------------------
  // EDIT FLOW
  // ------------------

  // open modal and prefill data
  const handleEdit = (u) => {
    setEditingUser(u);
    setUsername(u.username); // backend username stored as email
    setDesignation(u.designation);

    // ensure locations and outlets are arrays
    const userLocations = Array.isArray(u.locations)
      ? u.locations
      : u.locations
      ? [u.locations]
      : [];
    const userOutlets = Array.isArray(u.outlets)
      ? u.outlets
      : u.outlets
      ? [u.outlets]
      : [];

    setLocation(userLocations);
    setOutlets(userOutlets);

    const preselected = userLocations.flatMap((loc) =>
      (allOutletsByLocation[loc] || [])
        .filter((o) => userOutlets.includes(o))
        .map((o) => ({
          value: o,
          label: o,
          location: loc,
        }))
    );

    setSelectedOutletOptions(preselected);
    setFormError("");
    setSuccessMessage("");
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingUser(null);
    setFormError("");
    setSuccessMessage("");
    setLocation([]);
    setOutlets([]);
    setSelectedOutletOptions([]);
  };

  const handleUsernameChange = (e) => {
      const value = e.target.value;
        setUsername(value);
        setErrors((prev) => ({
        ...prev,
        username: validateEmail(value),
      }));
  };

  // save edited user
  const handleEditSubmit = async () => {
    if (!username || !designation || location.length === 0 || outlets.length === 0) {
      setFormError("Please fill in all fields");
      // Clear error message after 5 seconds
      setTimeout(() => {
        setFormError("");
      }, 1000);
      return;
    }

    try {
      setLoading(true);
      setFormError("");
      await axios.put(`/api/users/${editingUser.id}`, {
        username,
        designation,
        location, // array of locations
        outlets,  // array of outlets
        user_type: userType,
      });
      setSuccessMessage("User updated successfully");
      fetchUsers();
    } catch (err) {
      setFormError(
        err.response?.data?.error?.message || "Failed to update user"
      );
    } finally {
      setLoading(false);
      // Clear error message after 1 second
      setTimeout(() => {
        setFormError("");
        setSuccessMessage("");
        closeEditModal();
      }, 1000);
    }
  };

  // Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`/api/users/${id}`);
        fetchUsers();
      } catch (err) {
        alert("Failed to delete user");
      }  finally {
        setLoading(false);
      }
    }
  };

  // ------------------
  // React-Select handlers
  // ------------------
  const handleLocationChange = (selected) => {
    if (!selected || selected.length === 0) {
      setLocation([]);
      setSelectedOutletOptions([]);
      setOutlets([]);
      return;
    }

    let selectedLocationValues = [];

    // handle Select All
    if (selected.some((opt) => opt.value === "__ALL__")) {
      selectedLocationValues = locationOptions.map((opt) => opt.value);
    } else {
      selectedLocationValues = selected.map((opt) => opt.value);
    }

    setLocation(selectedLocationValues);

    // rebuild grouped outlet options is automatic via useMemo(groupedOutletOptions)
    // we just need to sync selected outlets with new locations:
    setSelectedOutletOptions((prevSelected) =>
      prevSelected.filter((opt) =>
        selectedLocationValues.includes(opt.location)
      )
    );

    setOutlets((prevOutlets) =>
      prevOutlets.filter((value) =>
        allOutletOptionsFlat.some(
          (o) => o.value === value && selectedLocationValues.includes(o.location)
        )
      )
    );
  };

  const handleOutletChange = (selected) => {
    if (!selected || selected.length === 0) {
      setSelectedOutletOptions([]);
      setOutlets([]);
      return;
    }

    // Select All clicked
    if (selected.some((opt) => opt.value === "__ALL__")) {
      setSelectedOutletOptions(allOutletOptionsFlat);
      setOutlets(allOutletValues);
      return;
    }

    // Normal selection
    const actualSelected = selected.filter((opt) => opt.value !== "__ALL__");
    setSelectedOutletOptions(actualSelected);
    setOutlets(actualSelected.map((opt) => opt.value));
  };

  const isAdmin = true; // or based on current logged-in user

  return (
    <div className="container">
      <h1 className="title">
        <center>User Management</center>
      </h1>
      <div className="top-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      {filteredUsers.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Designation</th>
              <th>User Type</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredUsers.map((u, index) => (
              <tr key={u.id ?? index}>
                <td>{index + 1}</td>
                <td>{u.username}</td>
                <td>{u.designation}</td>
                <td>{u.user_type}</td>
                <td>
                  <button className="edit-btn" onClick={() => handleEdit(u)}>
                    Edit
                  </button>

                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(u.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="no-data">No users found</div>
      )}

      {/* EDIT MODAL */}
      {showEditModal && (
        <div className="modal-backdrop">
          <div className="modal-content modal-scroll">
            <button className="modal-close" onClick={closeEditModal}>
              ×
            </button>
            <UserForm
              title="Edit User"
              subtitle="Update user details"
              buttonText="Save"
              userType={userType}
              setUserType={setUserType}
              username={username}
              setUsername={handleUsernameChange}
              password={""} // not editing password here
              setPassword={() => {}}
              errors={errors}
              designation={designation}
              setDesignation={setDesignation}
              location={location}
              locationOptions={locationOptions}
              locationOptionsWithSelectAll={locationOptionsWithSelectAll}
              handleLocationChange={handleLocationChange}
              outletOptionsWithSelectAll={outletOptionsWithSelectAll}
              handleOutletChange={handleOutletChange}
              selectedOutletOptions={selectedOutletOptions}
              onSubmit={handleEditSubmit}
              errorMessage={formError}
              successMessage={successMessage}
              showForgotPassword={false}
              loading={loading}
              isAdmin={isAdmin}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;